import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getClients } from '../services/clientService';
import {
  updateUser,
  deleteUser,
  getUserCount
} from '../services/userService';

// Clés de requête pour React Query
const CLIENTS_KEY = 'clients';
const CLIENT_COUNT_KEY = 'userCount';

/**
 * Hook de la liste « Clients » : pagination enrichie (user + site + création assistée).
 * Mutations : édition (updateUser) et suppression complète (deleteUser).
 */
export function useClients(
  pageSize = 10,
  page = 1,
  orderBy = 'createdAt',
  searchTerm = '',
  filter = 'all'
) {
  const queryClient = useQueryClient();

  const clientsQuery = useQuery({
    queryKey: [CLIENTS_KEY, pageSize, page, orderBy, searchTerm, filter],
    queryFn: async () => {
      const lastVisible = page > 1
        ? queryClient.getQueryData([CLIENTS_KEY, pageSize, page - 1, orderBy, searchTerm, filter])?.lastVisible
        : null;

      return getClients({ pageSize, page, lastVisible, orderByField: orderBy, searchTerm, filter });
    }
  });

  const countQuery = useQuery({
    queryKey: [CLIENT_COUNT_KEY],
    queryFn: getUserCount
  });

  const invalidateClients = () => {
    queryClient.invalidateQueries({ queryKey: [CLIENTS_KEY] });
  };

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, userData }) => updateUser(userId, userData),
    onSuccess: (_, variables) => {
      invalidateClients();
      queryClient.invalidateQueries({ queryKey: ['user', variables.userId] });
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      invalidateClients();
      queryClient.invalidateQueries({ queryKey: [CLIENT_COUNT_KEY] });
    }
  });

  return {
    clients: clientsQuery.data?.clients || [],
    lastVisible: clientsQuery.data?.lastVisible || null,
    isDataset: clientsQuery.data?.isDataset || false,
    datasetTotal: clientsQuery.data?.total ?? null,
    isLoading: clientsQuery.isLoading,
    isFetching: clientsQuery.isFetching,
    isError: clientsQuery.isError,
    error: clientsQuery.error,

    totalUsers: countQuery.data || 0,
    isLoadingCount: countQuery.isLoading,

    updateUser: updateUserMutation.mutate,
    deleteUser: deleteUserMutation.mutate,

    isUpdating: updateUserMutation.isPending,
    isDeleting: deleteUserMutation.isPending
  };
}
